import { useFeedback, useTranslation } from "@opendash/core";
import { _User } from "@opendash/plugin-parse/dist/types-generated";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";
import Parse from "parse";
import React, { useState } from "react";
import { useBikesAllowed } from "../hooks/useBikesAllowed";
import { useDirection } from "../hooks/useDirection";
import { useWheelchairAccessible } from "../hooks/useWheelchairAccessible";
import {
  Bikes_Allowed,
  Calendar,
  Direction,
  Route,
  Trip,
  Wheelchair_Accessible,
} from "../js/parsegenerated";
import { TourId } from "../types/TourId";
import { EndDateSelect } from "./EndDateSelect";
import { StartDateSelect } from "./StartDateSelect";
import Infolabel from "./UI/Infolabel";
import { WeekDaySelect } from "./WeekDaySelect";

export interface TripDataType {
  trip_headsign: string;
  trip_short_name: string;
  direction_id: number;
  wheelchair_accessible: number;
  bikes_allowed: number;
  start_date: Dayjs;
  end_date: Dayjs;
  weekdays: Array<string>;
}

export const TripCreateModal = (props: {
  open: boolean;
  route_id: string;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [directions] = useDirection();
  const [wheelchair_accessible] = useWheelchairAccessible();
  const [bikes_allowed] = useBikesAllowed();
  const [startdate, setStartdate] = useState<Dayjs>(dayjs());
  const [form] = Form.useForm();
  const [valuesHaveChanged, setValuesHaveChanged] = useState(false);

  const save = async () => {
    form.validateFields().then(async (values: TripDataType) => {
      setLoading(true);

      //   Find the selected route
      const route = await new Parse.Query(Route).get(props.route_id);

      //   Create the calendar entries
      const serivce: Calendar = new Calendar();
      const savedservice = await serivce.save({
        start_date: new Date(values.start_date.toISOString()),
        end_date: new Date(values.end_date.toISOString()),
        monday: values.weekdays?.includes("monday"),
        tuesday: values.weekdays?.includes("tuesday"),
        wednesday: values.weekdays?.includes("wednesday"),
        thursday: values.weekdays?.includes("thursday"),
        friday: values.weekdays?.includes("friday"),
        saturday: values.weekdays?.includes("saturday"),
        sunday: values.weekdays?.includes("sunday"),
        tenant: (Parse.User.current() as _User).get("tenant"),
      });

      if (route && savedservice) {
        const trip: Trip = new Trip();

        const bike = await new Parse.Query(Bikes_Allowed)
          .equalTo("value", values.bikes_allowed)
          .first();
        const direction = await new Parse.Query(Direction)
          .equalTo("value", values.direction_id)
          .first();
        const wheelchair = await new Parse.Query(Wheelchair_Accessible)
          .equalTo("value", values.wheelchair_accessible)
          .first();

        await trip.save({
          bikes_allowed: bike,
          direction_id: direction,
          trip_headsign: values.trip_headsign,
          trip_short_name: values.trip_short_name,
          wheelchair_accessible: wheelchair,
          route_id: route,
          service_id: savedservice,
          tenant: (Parse.User.current() as _User).get("tenant"),
          key: route.id + "_" + direction.id + "_" + values.trip_short_name,
        });
        message.success(t("gtfs:trip.create.modal.success"));
        props.onOk();
        form.resetFields();
      }

      setLoading(false);
    });
  };

  return (
    <Modal
      modalRender={(modal) => <div id={TourId.Trip.modal}>{modal}</div>}
      title={t("gtfs:trip.create.modal.title")}
      open={props.open}
      onOk={save}
      onCancel={() => {
        props.onCancel();
      }}
      width={"80%"}
      getContainer={false}
      footer={[
        <Button
          id={TourId.Trip.close}
          key="back"
          onClick={() => {
            props.onCancel();
          }}
        >
          {t("gtfs:global.button.cancel_label")}
        </Button>,
        <Button
          id={TourId.Trip.save}
          key="submit"
          type="primary"
          loading={loading}
          onClick={save}
        >
          {t("gtfs:global.button.save_label")}
        </Button>,
      ]}
    >
      <Form
        autoComplete="off"
        form={form}
        layout="vertical"
        onValuesChange={() => {
          setValuesHaveChanged(true);
        }}
      >
        <Space direction="vertical" size="large">
          <Form.Item
            name="trip_headsign"
            label={
              <Infolabel
                title={t("gtfs:trip.create.modal.form.trip_headsign.label")}
                info={t("gtfs:trip.create.modal.form.trip_headsign.info")}
              />
            }
            help={t("gtfs:trip.create.modal.form.trip_headsign.help")}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="trip_short_name"
            label={
              <Infolabel
                title={t("gtfs:trip.create.modal.form.trip_short_name.label")}
                info={t("gtfs:trip.create.modal.form.trip_short_name.info")}
              />
            }
            help={t("gtfs:trip.create.modal.form.trip_short_name.help")}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="direction_id"
            label={
              <Infolabel
                title={t("gtfs:trip.create.modal.form.direction_id.label")}
                info={t("gtfs:trip.create.modal.form.direction_id.info")}
              />
            }
            help={t("gtfs:trip.create.modal.form.direction_id.help")}
            initialValue={0}
          >
            <Select
              options={directions.map((direction) => {
                return {
                  value: direction.value,
                  label: direction.value + ": " + direction.description,
                };
              })}
            />
          </Form.Item>
          <Form.Item
            name="wheelchair_accessible"
            label={t("gtfs:trip.create.modal.form.wheelchair_accessible.label")}
            help={t("gtfs:trip.create.modal.form.wheelchair_accessible.help")}
            initialValue={0}
          >
            <Select
              options={wheelchair_accessible.map((wa) => {
                return {
                  value: wa.value,
                  label: wa.value + ": " + wa.description,
                };
              })}
            />
          </Form.Item>
          <Form.Item
            name="bikes_allowed"
            label={t("gtfs:trip.create.modal.form.bikes_allowed.label")}
            help={t("gtfs:trip.create.modal.form.bikes_allowed.help")}
            initialValue={0}
          >
            <Select
              options={bikes_allowed.map((ba) => {
                return {
                  value: ba.value,
                  label: ba.value + ": " + ba.description,
                };
              })}
            />
          </Form.Item>

          <Form.Item name="weekdays">
            <WeekDaySelect />
          </Form.Item>
          <Form.Item
            label={t("gtfs:trip.create.modal.form.service.start_date.label")}
            help={t("gtfs:trip.create.modal.form.service.start_date.help")}
            name="start_date"
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:trip.create.modal.form.service.start_date.rules.required"
                ),
              },
            ]}
          >
            <StartDateSelect
              onChange={(newdate: Dayjs) => {
                setStartdate(newdate);
              }}
            />
          </Form.Item>

          <Form.Item
            label={t("gtfs:trip.create.modal.form.service.end_date.label")}
            help={t("gtfs:trip.create.modal.form.service.end_date.help")}
            name="end_date"
            rules={[
              {
                required: true,
                message: t(
                  "gtfs:trip.create.modal.form.service.end_date.rules.required"
                ),
              },
            ]}
          >
            <EndDateSelect startdate={startdate} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};
